import { type MouseEvent, useCallback } from 'react'

import { format } from 'date-fns/format'
import { isSameYear } from 'date-fns/isSameYear'
import { isToday } from 'date-fns/isToday'
import { isYesterday } from 'date-fns/isYesterday'
import { useUnit } from 'effector-react'
import { useMutation } from 'react-query'

import { useToast } from '@/components/ui/toast.tsx'
import { Tooltip } from '@/components/ui/tooltip'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { useHasChatUNreadMessages } from '@/models/chattingUI/chats'
import { deleteChat } from '@/models/chattingUI/chats/chats.ts'
import { useMessageDetails } from '@/models/chattingUI/messages/messages.ts'
import { selectorHasUnreadMessagesByChatId } from '@/models/chattingUI/unreadChatMessages'
import { $creatorId, $currentCreator } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs.ts'
import { $hasClearPermission } from '@/models/permissions.ts'
import { chatsService } from '@/modules/chatting/service/chats.ts'
import { unescapeHtmlText } from '@/modules/chatting/ui/chat-window/fan-info/utils.ts'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider'
import { handleError } from '@/utils/error'
import { classNames } from '@/utils/helpers'

import ClearChatIcon from '../../icons/clear-chat.svg'
import MutedIcon from '../../icons/muted.svg'
import ProhibitionIcon from '../../icons/prohibition.svg'
import type { Chat, FanCountdown } from '../../types'
import { TimeDifference } from '../components/TimeDifference'
import { IconButton } from '../components/icon-button'
import { getIsChatRestricted, getSpendingConfig } from '../utils'
import './chat-info.css'
import { MessagePreview } from './message-preview'

interface Props {
  chat: Chat
  className?: string
  countdown?: FanCountdown
  isTyping?: boolean
}

export const ChatInfo = ({ chat, className, countdown, isTyping }: Props) => {
  const toast = useToast()
  const creatorId = useUnit($creatorId)
  const creator = useUnit($currentCreator)
  const { fanNumberingEnabled } = useSettingsContext()
  const clearChatMutation = useMutation(chatsService.clearChat, {
    onSuccess: (_, { userId }) => {
      toast.success('Chat was successfully cleared!')
      deleteChat(userId)
    },
    onError: (error) => {
      handleError(error, 'Something went wrong while clearing chat')
    },
  })

  const { withUser: fan, lastMessageId, isMutedNotifications } = chat
  const lastMessage = useMessageDetails(lastMessageId)
  const isRestricted = getIsChatRestricted(chat)

  const isLastMessageUnread = useHasChatUNreadMessages(chat.withUser.id)
  const hasUnseenMessages = useUnit(
    selectorHasUnreadMessagesByChatId(chat.withUser.id),
  )

  const hasUnreadMessage = isLastMessageUnread || hasUnseenMessages

  const handleClickClear = useCallback(
    (event: MouseEvent<SVGElement>) => {
      event.stopPropagation()

      openDialog({
        type: DIALOG_TYPE.CONFIRMATION,
        title: 'Are you sure you want to clear chat?',
        onConfirm: () =>
          clearChatMutation.mutateAsync({
            userId: fan.id,
            creatorId: creatorId!,
          }),
      })
    },
    [clearChatMutation, creatorId, fan.id],
  )

  const { fanNumber, lastPPVPurchase } = fan
  const spendingConfig = getSpendingConfig(lastPPVPurchase)

  const hasClearPermission = useUnit($hasClearPermission)

  return (
    <div
      className={classNames('w-full space-y-0.5 overflow-hidden', className)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 overflow-hidden text-ellipsis">
          {fanNumberingEnabled && fan.fanNumber && (
            <span className="text-primary-500 font-semibold">
              [{fanNumber}]
            </span>
          )}

          <span className="whitespace-nowrap font-medium">
            {unescapeHtmlText(fan.displayName || fan.name)}
          </span>

          {fan.displayName && (
            <span className="text-secondary-300 overflow-hidden text-ellipsis whitespace-nowrap">
              ({unescapeHtmlText(fan.name)})
            </span>
          )}
        </div>
        <div className="flex gap-1">
          {!!lastMessage && (
            <span
              className={classNames(
                'text-secondary-300 shrink-0 whitespace-nowrap text-xs font-medium',
                {
                  'text-primary-500': hasUnreadMessage && !isMutedNotifications,
                },
              )}
            >
              <TimeDifference
                date={lastMessage?.createdAt}
                calculator={timeDifferenceCalculator}
              />
            </span>
          )}
          {hasClearPermission && (
            <IconButton
              tooltip="Clear Chat"
              icon={ClearChatIcon}
              onClick={handleClickClear}
            />
          )}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        {isTyping ? (
          <span className="typing-animation text-sm">is typing</span>
        ) : (
          <MessagePreview
            message={lastMessage}
            isFromCreator={lastMessage?.fromUser.id === creator?.onlyfansId}
            countdown={countdown}
            isChatMuted={isMutedNotifications}
            isNew={hasUnreadMessage}
          />
        )}
        <div className="flex items-center gap-1.5">
          {spendingConfig && (
            <Tooltip content={spendingConfig.tooltip}>
              <div
                className={classNames(
                  'relative rounded-full pl-1 pr-5 text-xs font-medium leading-[18px] text-white',
                  spendingConfig.bgClassName,
                )}
              >
                {spendingConfig.text}
                <spendingConfig.Icon className="absolute -right-[1px] -top-[1px] bottom-0" />
              </div>
            </Tooltip>
          )}
          {isMutedNotifications && <MutedIcon className="text-secondary-200" />}
          {hasUnreadMessage && !isRestricted && (
            <div
              className={classNames(
                'bg-primary-500 h-2.5 w-2.5 rounded-full',
                isMutedNotifications && 'bg-secondary-300',
              )}
            />
          )}
          {isRestricted && (
            <Tooltip content="User is restricted">
              <div>
                <ProhibitionIcon className="text-secondary-300 h-4 w-4" />
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  )
}

function timeDifferenceCalculator(date: Date): string {
  if (isToday(date)) {
    return format(date, 'h:mm aaa')
  }

  if (isYesterday(date)) {
    return 'Yesterday'
  }

  if (isSameYear(new Date(), date)) {
    return format(date, 'MMM d')
  }

  return format(date, 'MMM d, yyyy')
}
